import React, { useState, useEffect } from 'react'
import { 
  ArrowLeft,
  Save,
  RotateCcw
} from 'lucide-react'
import { Breadcrumb } from '../../components/Common'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import autoIncrementService from '../../services/autoIncrementService'
import paymentTermsMasterService from '../../services/paymentTermsMasterService'

const PaymentTermsMasterForm = () => {
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    paymentTermsCode: '',
    order: '',
    description: '',
    status: 'Active'
  })
  const [isLoadingPaymentTermsCode, setIsLoadingPaymentTermsCode] = useState(true)

  // Fetch auto-increment number for PAYMENT-TERMS
  const fetchPaymentTermsCode = async () => {
    try {
      setIsLoadingPaymentTermsCode(true)
      const response = await autoIncrementService.getCurrentValue('PAYMENT-TERMS')
      console.log('Auto-increment response:', response)
      
      // Handle different response structures
      let sequence = null
      
      if (response.success && response.data) {
        sequence = response.data
      } else if (response.data) {
        // Sometimes the data is directly in response.data
        sequence = response.data
      } else if (response) {
        // Sometimes the response itself is the data
        sequence = response
      }
      
      console.log('Sequence data:', sequence)
      
      if (sequence) {
        console.log('Sequence data:', sequence)
        
        // Use the formattedValue directly from backend
        if (sequence.formattedValue) {
          console.log('Using backend formatted value:', sequence.formattedValue)
          setFormData(prev => ({
            ...prev,
            paymentTermsCode: sequence.formattedValue
          }))
        } else {
          console.error('No formattedValue found in sequence:', sequence)
          // Set a default payment terms code without showing error
          setFormData(prev => ({
            ...prev,
            paymentTermsCode: 'PT0001'
          }))
        }
      } else {
        console.error('No sequence data found in response:', response)
        // Set a default payment terms code without showing error
        setFormData(prev => ({
          ...prev,
          paymentTermsCode: 'PT0001'
        }))
      }
    } catch (error) {
      console.error('Error fetching payment terms code:', error)
      // Set a default payment terms code without showing error
      setFormData(prev => ({
        ...prev,
        paymentTermsCode: 'PT0001'
      }))
    } finally {
      setIsLoadingPaymentTermsCode(false)
    }
  }

  // Load auto-increment code on component mount
  useEffect(() => {
    fetchPaymentTermsCode()
  }, [])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSave = async () => {
    try {
      console.log('Saving Payment Terms Master data:', formData)
      
      // Get the next value when actually saving
      const response = await autoIncrementService.getNextValue('PAYMENT-TERMS')
      if (response && response.success && response.data && response.data.formattedValue) {
        // Update the payment terms code with the incremented value
        const updatedFormData = {
          ...formData,
          paymentTermsCode: response.data.formattedValue
        }
        console.log('Updated form data with new payment terms code:', updatedFormData)
        
        // Call the payment terms master API to save the data
        const saveResponse = await paymentTermsMasterService.createPaymentTerms(updatedFormData)
        
        if (saveResponse && saveResponse.success) {
          toast.success('Payment Terms Master saved successfully!')
          navigate('/sales/payment-terms-master')
        } else {
          toast.error(saveResponse?.message || 'Failed to save Payment Terms Master')
        }
      } else {
        toast.error('Failed to generate payment terms code')
      }
    } catch (error) {
      console.error('Error saving Payment Terms Master:', error)
      if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Invalid data provided')
      } else if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
        navigate('/login')
      } else {
        toast.error('Failed to save Payment Terms Master')
      }
    }
  }

  const handleReset = () => {
    setFormData({
      paymentTermsCode: '',
      order: '',
      description: '',
      status: 'Active'
    })
  }

  const handleBack = () => {
    navigate('/sales/payment-terms-master')
  }

  // Breadcrumb items
  const breadcrumbItems = [
    {
      label: 'Sales',
      path: '/sales',
      onClick: () => navigate('/sales')
    },
    {
      label: 'Masters',
      path: '/sales',
      onClick: () => navigate('/sales')
    },
    {
      label: 'Payment Terms Master',
      path: '/sales/payment-terms-master',
      isCurrent: true
    }
  ]

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Payment Terms Master</h1>
          <p>Manage payment terms master data</p>
        </div>
      </div>

      {/* Form Section */}
      <div className="form-container">
        <div className="sac-form-header">
          <h2>Payment Terms Master</h2>
        </div>
          
        <form className="form-content">
          <div className="section-header"><h3>Basic Information</h3></div>
          {/* Row 1: Payment Terms Code, Order, Description, Status */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="paymentTermsCode" className="form-label required">
                Payment Terms Code
              </label>
              <input
                type="text"
                id="paymentTermsCode"
                name="paymentTermsCode"
                value={isLoadingPaymentTermsCode ? 'Generating...' : formData.paymentTermsCode}
                readOnly
                className="form-input"
                placeholder="Enter payment terms code"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="order" className="form-label required">
                Order
              </label>
              <input
                type="number"
                id="order"
                name="order"
                value={formData.order}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter order number"
                min="1"
                step="1"
              />
            </div>

            <div className="form-group">
              <label htmlFor="description" className="form-label required">
                Description
              </label>
              <input
                type="text"
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter description"
              />
            </div>

            <div className="form-group">
              <label htmlFor="status" className="form-label required">
                Status
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="form-input"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="form-actions">
            <button
              type="button"
              onClick={handleReset}
              className="btn-secondary"
            >
              <RotateCcw size={16} />
              Reset
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="btn-primary"
            >
              <Save size={16} />
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default PaymentTermsMasterForm